import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ImgixImage } from '@air/component-imgix-image';

import { BOARD_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { getPreferenceImgixFit, useThumbnailPreference } from '~/hooks/useThumbnailPreference';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';

export type BoardCellThumbnailProps = {
  thumbnail: string | undefined;
  title: string;
};

export const BoardCellThumbnail = memo(({ title, thumbnail }: BoardCellThumbnailProps) => {
  const fallbackUrl = thumbnail && getCloudFrontUrlFromImgixUrl(thumbnail);

  const { getPreference } = useThumbnailPreference();
  const boardId = useSelector(CentralizedBoardSelectors.boardId);
  const thumbnailPreference = useMemo(() => getPreference(boardId), [boardId, getPreference]);

  return (
    <div className="flex h-full items-center justify-center rounded-4 border border-pigeon-100 border-solid bg-white p-1">
      {fallbackUrl ? (
        <ImgixImage
          fallbackUrl={fallbackUrl}
          alt={title}
          src={thumbnail}
          imgixParams={{
            fit: getPreferenceImgixFit(thumbnailPreference),
            cs: 'origin',
          }}
          style={{
            borderRadius: '2px',
            objectFit: thumbnailPreference === 'default' ? 'cover' : 'contain',
            ...BOARD_THUMBNAIL_DIMENSIONS,
          }}
          {...BOARD_THUMBNAIL_DIMENSIONS}
        />
      ) : (
        <div className="size-full bg-pigeon-100" />
      )}
    </div>
  );
});

BoardCellThumbnail.displayName = 'BoardCellThumbnail';
